import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Button, Spinner, Card } from 'react-bootstrap';
import { format, parseISO } from 'date-fns';
import DOMPurify from 'dompurify';
import SessionGroupCard from './SessionGroupCard';
import PastSessionGroups from './PastSessionGroups';
import './AIBootcampProPortal.css';
import Loading from '../../components/Loading';
import { SparkleButton } from '../../components/SparkleEffect';
import { pushEvent, ANALYTICS_EVENTS } from '../../utils/analytics';
import useUser from '../../hooks/useUser';
import { usePaymentStatus } from '../../contexts/PaymentStatusContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import { buildAuthOverlayRouteState } from '../../utils/authOverlayNavigation';

// Helper functions for calendar integration
const getUserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

const formatICSDate = (date) => {
  const pad = (num) => (num < 10 ? '0' : '') + num;
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}T${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
};

const generateGroupICalFile = (sessions, group) => {
  if (!sessions || sessions.length === 0) return '#';
  try {
    const timeZone = getUserTimeZone();
    const events = sessions.map((session, idx) => {
      if (!session.scheduledAt) return '';
      const startDate = new Date(session.scheduledAt);
      const endDate = new Date(startDate);
      endDate.setHours(endDate.getHours() + 1);
      
      // PROMINENT JOIN URL EVERYWHERE!
      const joinUrlText = session.joinUrl ? `🔗 ZOOM JOIN LINK: ${session.joinUrl}` : '';
      
      let descriptionText = `🎯 AI BOOTCAMP PRO SESSION ${idx + 1}`;
      if (session.joinUrl) {
        descriptionText += `\n\n🚀 JOIN THE SESSION:\n${session.joinUrl}\n\n🔗 ZOOM LINK:\n${session.joinUrl}\n\n📱 CLICK TO JOIN:\n${session.joinUrl}`;
      }
      if (session.description || group?.description) {
        descriptionText += `\n\n📋 SESSION DETAILS:\n${session.description || group?.description}`;
      }
      if (session.joinUrl) {
        descriptionText += `\n\n⚡ QUICK ACCESS:\n${session.joinUrl}`;
      }
      
      const locationText = session.joinUrl 
        ? `🔗 Zoom Meeting: ${session.joinUrl}` 
        : 'Online via Zoom (Link TBA)';
      
      return [
        'BEGIN:VEVENT',
        `SUMMARY:${session.title || `🎯 AI Bootcamp Pro - Session ${idx + 1}`}${session.joinUrl ? ` - ${session.joinUrl}` : ''}`,
        `DTSTART;TZID=${timeZone}:${formatICSDate(startDate)}`,
        `DTEND;TZID=${timeZone}:${formatICSDate(endDate)}`,
        `DESCRIPTION:${descriptionText}`,
        `LOCATION:${locationText}`,
        `URL:${session.joinUrl || ''}`,
        `UID:${session.id || `${group?.id || 'group'}-${idx}`}@lumiwealth.com`,
        'STATUS:CONFIRMED',
        'SEQUENCE:0',
        'BEGIN:VALARM',
        'TRIGGER:-PT15M',
        'ACTION:DISPLAY',
        `DESCRIPTION:🚨 AI Bootcamp starting in 15 minutes! ${joinUrlText}`,
        'END:VALARM',
        'BEGIN:VALARM',
        'TRIGGER:-PT5M',
        'ACTION:DISPLAY',
        `DESCRIPTION:🔥 Session starts in 5 minutes! ${joinUrlText}`,
        'END:VALARM',
        'END:VEVENT'
      ].join('\r\n');
    }).join('\r\n');
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'CALSCALE:GREGORIAN',
      `X-WR-TIMEZONE:${timeZone}`,
      'METHOD:PUBLISH',
      events,
      'END:VCALENDAR'
    ].join('\r\n');
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    return URL.createObjectURL(blob);
  } catch (e) {
    return '#';
  }
};

const generateGoogleCalLink = (session) => {
  if (!session || !session.scheduledAt) return '#';
  const startDate = new Date(session.scheduledAt);
  const endDate = new Date(startDate);
  endDate.setHours(endDate.getHours() + 1);
  const pad = (num) => (num < 10 ? '0' : '') + num;
  const formatDate = (date) =>
    `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}T${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
  
  // SUPER PROMINENT JOIN URL FOR GOOGLE CALENDAR
  let descriptionText = `🎯 AI BOOTCAMP PRO SESSION`;
  if (session.joinUrl) {
    descriptionText += `\n\n🚀 JOIN THE SESSION:\n${session.joinUrl}\n\n🔗 ZOOM LINK:\n${session.joinUrl}\n\n📱 CLICK TO JOIN:\n${session.joinUrl}`;
  }
  if (session.description) {
    descriptionText += `\n\n📋 SESSION DETAILS:\n${session.description}`;
  }
  if (session.joinUrl) {
    descriptionText += `\n\n⚡ QUICK ACCESS:\n${session.joinUrl}`;
  }
  
  const locationText = session.joinUrl 
    ? `🔗 Zoom Meeting: ${session.joinUrl}` 
    : 'Online via Zoom (Link TBA)';
  
  const sessionTitle = session.joinUrl 
    ? `${session.title || 'AI Bootcamp Pro Session'} - ${session.joinUrl}`
    : session.title || 'AI Bootcamp Pro Session';
  
  const timeZone = getUserTimeZone();
  const googleCalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(sessionTitle)}&dates=${formatDate(startDate)}/${formatDate(endDate)}&details=${encodeURIComponent(descriptionText)}&location=${encodeURIComponent(locationText)}&ctz=${encodeURIComponent(timeZone)}`;
  return googleCalUrl;
};

const BOOTCAMP_TOKEN_TIMEOUT_MS = 8000;

const withTimeout = (promise, timeoutMs, errorMessage) => {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => reject(new Error(errorMessage)), timeoutMs);

    Promise.resolve(promise)
      .then((value) => {
        clearTimeout(timeoutId);
        resolve(value);
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        reject(error);
      });
  });
};

const AIBootcampProPortal = () => {
  const {
    user,
    isLoading: authLoadingFromAuth0,
    loginWithRedirect,
    getApiToken,
    isAuthenticated,
  } = useUser();

  const { productSlugs = [] } = usePaymentStatus();

  const navigate = useNavigate();
  const location = useLocation();

  // Post-purchase first-run panel. Checkout sends buyers here with ?welcome=1
  // so they land inside the product instead of on a static thank-you page.
  const [showWelcome, setShowWelcome] = useState(
    () => new URLSearchParams(location.search).get('welcome') === '1'
  );
  const dismissWelcome = useCallback(() => {
    setShowWelcome(false);
    const params = new URLSearchParams(location.search);
    params.delete('welcome');
    const qs = params.toString();
    navigate({ pathname: location.pathname, search: qs ? `?${qs}` : '' }, { replace: true });
  }, [location.pathname, location.search, navigate]);

  const [courseData, setCourseData] = useState(null);
  const [sessionGroup, setSessionGroup] = useState(null);
  const [isLoadingCourse, setIsLoadingCourse] = useState(true);
  const [serviceUnavailable, setServiceUnavailable] = useState(false);
  const [courseLoadError, setCourseLoadError] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [guestInfo, setGuestInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [formErrors, setFormErrors] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [apiError, setApiError] = useState('');
  const [pastSessionGroups, setPastSessionGroups] = useState([]);
  const [futureSessionGroups, setFutureSessionGroups] = useState([]);
  const [registeredCohorts, setRegisteredCohorts] = useState({});

  const registrationConfirmationRef = useRef(null);

  const overallIsLoading = authLoadingFromAuth0 || isLoadingCourse;
  const hasBootcampContentAccess = courseData?.userHasAccess === true;
  const hasLiveBootcampAccess = productSlugs.includes('ai-bootcamp');

  // Scroll to confirmation after registration
  React.useEffect(() => {
    if (isRegistered && registrationConfirmationRef.current) {
      requestAnimationFrame(() => {
        registrationConfirmationRef.current.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
        window.scrollBy({ top: -80, left: 0, behavior: 'smooth' });
      });
    }
  }, [isRegistered]);

  useEffect(() => {
    if (sessionGroup && sessionGroup.id) {
      const storedRegistration = localStorage.getItem(`ai-bootcamp-registered-${sessionGroup.id}`);
      if (storedRegistration) {
        try {
          const registrationData = JSON.parse(storedRegistration);
          // FIXED: Use unified user ID for both Auth0 and custom JWT
          const currentUserId = user?.sub || user?.id || user?.userId;
          if (isAuthenticated && registrationData.userId === currentUserId) {
            setIsRegistered(true);
          }
        } catch (err) {
          localStorage.removeItem(`ai-bootcamp-registered-${sessionGroup.id}`);
        }
      }
    }
  }, [sessionGroup, isAuthenticated, user]);

  useEffect(() => {
    let mounted = true;
    const fetchBootcampInfo = async () => {
      setIsLoadingCourse(true);
      setServiceUnavailable(false);
      setCourseLoadError('');
      try {
        let headers = {};
        
        // CRITICAL FIX: Only proceed if authenticated and token is available
        if (!isAuthenticated) {
          console.log('[AIBootcamp] ❌ User not authenticated, aborting API calls');
          if (mounted) setIsLoadingCourse(false);
          return;
        }
        
        console.log('[AIBootcamp] User is authenticated, attempting to get API token...');
        try {
          const token = await withTimeout(
            getApiToken(),
            BOOTCAMP_TOKEN_TIMEOUT_MS,
            'Timed out waiting for Auth0 access token'
          );
          if (!token) {
            console.error('[AIBootcamp] ❌ CRITICAL: getApiToken() returned null/undefined - aborting');
            if (mounted) {
              setCourseLoadError('We could not verify your bootcamp access. Please refresh or sign in again.');
              setServiceUnavailable(true);
              setIsLoadingCourse(false);
            }
            return;
          }
          headers = { 'Authorization': `Bearer ${token}` };
          console.log('[AIBootcamp] ✅ Successfully got auth token for ALL API calls');
        } catch (tokenError) {
          console.error('[AIBootcamp] ❌ Failed to get API token - aborting:', tokenError);
          if (mounted) {
            setCourseLoadError('We could not verify your bootcamp access. Please refresh or sign in again.');
            setServiceUnavailable(true);
            setIsLoadingCourse(false);
          }
          return;
        }
        const courseId = process.env.REACT_APP_AI_BOT_BUILDER_BOOTCAMP_COURSE_ID;
        if (!courseId) throw new Error('AI Bootcamp course ID not configured');
        const apiUrl = getApiBaseUrl();

        // All API calls now use the same authenticated headers object

        console.log('[AIBootcamp] Making course API call with headers:', Object.keys(headers));
        const courseResponse = await fetch(
          `${apiUrl}/courses/${courseId}`,
          { headers } // FIXED: Use authenticated headers instead of courseApiHeaders
        );
        if (!courseResponse.ok) throw new Error(`Failed to fetch course: ${courseResponse.status}`);
        const courseDataFromApi = await courseResponse.json();
        if (mounted) {
          setCourseData(courseDataFromApi);
          try {
            console.log('[AIBootcamp] Making session groups API call with headers:', Object.keys(headers));
            const sessionGroupsResponse = await fetch(
              `${apiUrl}/courses/session-groups?courseId=${courseId}`,
              { headers }
            );
            if (!sessionGroupsResponse.ok) throw new Error(`Failed to fetch session groups: ${sessionGroupsResponse.status}`);
            const sessionGroupsData = await sessionGroupsResponse.json();
            const { sessionGroups } = sessionGroupsData;
            if (sessionGroups && sessionGroups.length > 0) {
              const isSessionGroupPast = (group) => {
                if (!group.sessions || group.sessions.length === 0) return false;

                const sortedSessions = [...group.sessions].sort((a, b) =>
                  new Date(b.scheduledAt) - new Date(a.scheduledAt)
                );

                const lastSession = sortedSessions[0];
                const lastSessionDate = new Date(lastSession.scheduledAt);

                const durationMinutes = lastSession.durationMinutes || 60;
                const sessionEndTime = new Date(lastSessionDate.getTime() + durationMinutes * 60 * 1000);
                const now = new Date();

                return now > sessionEndTime;
              };

              const allGroupDetails = await Promise.all(
                sessionGroups.map(async (group) => {
                  try {
                    console.log(`[AIBootcamp] 🔍 Fetching session group details for ${group.id} with authentication`);
                    const response = await fetch(
                      `${apiUrl}/courses/session-groups/${group.id}`,
                      { headers } // Use authenticated headers instead of courseApiHeaders
                    );
                    console.log(`[AIBootcamp] 📡 Session group ${group.id} fetch status:`, response.status);
                    if (response.ok) {
                      const groupData = await response.json();
                      console.log(`[AIBootcamp] 🎯 Session group ${group.id} - has join URLs:`, 
                        groupData.sessions?.some(s => s.joinUrl) ? 'YES' : 'NO');
                      return groupData;
                    }
                  } catch (error) {
                    console.error(`Error fetching bootcamp details for session group ${group.id}:`, error);
                  }
                  return null;
                })
              );

              const validGroupDetails = allGroupDetails.filter(Boolean);

              const pastGroups = [];
              const futureGroups = [];
              let currentGroup = null;

              validGroupDetails.forEach(group => {
                if (isSessionGroupPast(group)) {
                  pastGroups.push(group);
                } else {
                  if (!currentGroup) {
                    currentGroup = group;
                  } else if (group.nextSession && currentGroup.nextSession) {
                    const groupDate = new Date(group.nextSession.scheduledAt);
                    const currentDate = new Date(currentGroup.nextSession.scheduledAt);

                    if (groupDate < currentDate) {
                      futureGroups.push(currentGroup);
                      currentGroup = group;
                    } else {
                      futureGroups.push(group);
                    }
                  } else {
                    if (group.nextSession && !currentGroup.nextSession) {
                      futureGroups.push(currentGroup);
                      currentGroup = group;
                    } else {
                      futureGroups.push(group);
                    }
                  }
                }
              });

              setSessionGroup(currentGroup);
              setFutureSessionGroups(futureGroups);
              setPastSessionGroups(pastGroups);

              if (currentGroup && currentGroup.registration && isAuthenticated) {
                setIsRegistered(true);
              }
            } else {
              console.log('No bootcamp session groups found');
            }
          } catch (e) {
            console.error("Error fetching session groups details:", e);
          }
          if (mounted) setIsLoadingCourse(false);
        }
      } catch (err) {
        if (mounted) {
          setCourseLoadError('We could not load bootcamp information right now. Please refresh or try again shortly.');
          setServiceUnavailable(true);
          setIsLoadingCourse(false);
        }
      }
    };
    
    // Only fetch if user is authenticated (email no longer required)
    if (isAuthenticated) {
      console.log('[AIBootcamp] User authenticated, fetching bootcamp info:', { 
        isAuthenticated, 
        hasUserEmail: !!user?.email,
        userId: user?.id || 'unknown'
      });
      fetchBootcampInfo();
    } else {
      console.log('[AIBootcamp] User not authenticated, showing loading state:', { 
        isAuthenticated, 
        hasUserEmail: !!user?.email
      });
      setIsLoadingCourse(false);
    }
    
    return () => { mounted = false; };
  }, [isAuthenticated, user?.email, getApiToken]);

  // CRITICAL FIX: Clear registration data when user changes
  // This prevents bootcamp registration data from persisting across different users
  useEffect(() => {
    const currentUserId = user?.sub || user?.id || user?.userId;
    
    if (isAuthenticated && currentUserId) {
      // Check all bootcamp registration data and clear any that belongs to different users
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('ai-bootcamp-registered-')) {
          try {
            const storedData = JSON.parse(localStorage.getItem(key));
            if (storedData.userId && storedData.userId !== currentUserId) {
              console.log(`[AIBootcamp] Clearing registration data from previous user: ${key}`);
              localStorage.removeItem(key);
            }
          } catch {
            // If data is corrupted, remove it
            localStorage.removeItem(key);
          }
        }
      });
    } else if (!isAuthenticated) {
      // If user is not authenticated, clear all bootcamp registration data
      // since bootcamp requires authentication
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('ai-bootcamp-registered-')) {
          console.log(`[AIBootcamp] Clearing registration data for unauthenticated user: ${key}`);
          localStorage.removeItem(key);
        }
      });
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    const checkRegistrationStatuses = () => {
      if (!futureSessionGroups || futureSessionGroups.length === 0) return;
      const registrationStatuses = {};
      if (sessionGroup?.id && isRegistered) {
        registrationStatuses[sessionGroup.id] = true;
      }
      futureSessionGroups.forEach(group => {
        const storedRegistration = localStorage.getItem(`ai-bootcamp-registered-${group.id}`);
        if (storedRegistration) {
          try {
            const registrationData = JSON.parse(storedRegistration);
            // CRITICAL FIX: Only show as registered if user IS authenticated AND userIds match
            // This prevents showing registration status from previous users
            const currentUserId = user?.sub || user?.id || user?.userId;
            if (isAuthenticated && registrationData.userId === currentUserId) {
              registrationStatuses[group.id] = true;
            } else if (isAuthenticated && registrationData.userId !== currentUserId) {
              // Clear registration data from a different user
              console.log(`[AIBootcamp] Clearing registration data from different user: ${registrationData.userId} vs ${currentUserId}`);
              localStorage.removeItem(`ai-bootcamp-registered-${group.id}`);
            }
          } catch {
            localStorage.removeItem(`ai-bootcamp-registered-${group.id}`);
          }
        } else {
          if (group.isRegistered && isAuthenticated) {
            registrationStatuses[group.id] = true;
          }
        }
      });
      setRegisteredCohorts(registrationStatuses);
    };
    checkRegistrationStatuses();
  }, [futureSessionGroups, sessionGroup, isRegistered, isAuthenticated, user]);

  // Re-fetch ALL session groups data when registration status changes (to get join URLs)
  useEffect(() => {
    const refreshAllSessionGroupsData = async () => {
      if (isRegistered && sessionGroup?.id && isAuthenticated) {
        try {
          console.log('[AIBootcamp] 🔄 Re-fetching ALL session groups data with authentication for join URLs...');
          const token = await getApiToken();
          console.log('[AIBootcamp] 🔑 Got API token for complete data refresh');
          
          // Fetch session groups list (this includes proper registration checks)
          const courseId = process.env.REACT_APP_AI_BOT_BUILDER_BOOTCAMP_COURSE_ID;
          const apiUrl = getApiBaseUrl();
          const sessionGroupsResponse = await fetch(
            `${apiUrl}/courses/session-groups?courseId=${courseId}`,
            { 
              headers: { 
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              } 
            }
          );
          
          console.log('[AIBootcamp] 📡 Session groups list fetch status:', sessionGroupsResponse.status);
          
          if (sessionGroupsResponse.ok) {
            const sessionGroupsData = await sessionGroupsResponse.json();
            const { sessionGroups } = sessionGroupsData;
            
            if (sessionGroups && sessionGroups.length > 0) {
              // Now fetch detailed data for each group with authentication
              const groupDetails = await Promise.all(
                sessionGroups.map(async (group) => {
                  try {
                    console.log(`[AIBootcamp] 🔍 Re-fetching details for group ${group.id} with auth`);
                    const response = await fetch(
                      `${apiUrl}/courses/session-groups/${group.id}`,
                      { 
                        headers: { 
                          'Authorization': `Bearer ${token}`,
                          'Content-Type': 'application/json'
                        } 
                      }
                    );
                    if (response.ok) {
                      const groupData = await response.json();
                      console.log(`[AIBootcamp] 🎯 Group ${group.id} refresh - has join URLs:`, 
                        groupData.sessions?.some(s => s.joinUrl) ? 'YES' : 'NO');
                      return groupData;
                    }
                  } catch (error) {
                    console.error(`Error re-fetching group ${group.id}:`, error);
                  }
                  return null;
                })
              );
              
              // Find our current session group in the refreshed data
              const currentGroupRefreshed = groupDetails.find(g => g && g.id === sessionGroup.id);
              if (currentGroupRefreshed) {
                console.log('[AIBootcamp] ✅ Found refreshed current session group with join URLs');
                setSessionGroup(currentGroupRefreshed);
              }
            }
          } else {
            console.error('[AIBootcamp] ❌ Failed to fetch session groups list:', sessionGroupsResponse.status);
          }
        } catch (error) {
          console.error('[AIBootcamp] Error refreshing session groups data:', error);
        }
      }
    };
    
    refreshAllSessionGroupsData();
  }, [isRegistered, sessionGroup?.id, isAuthenticated, getApiToken]);

  const handleRefresh = () => window.location.reload();

  const formatWorkshopDate = (dateString) => {
    try {
      const date = parseISO(dateString);
      const timeZoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const timeZoneAbbr = new Intl.DateTimeFormat('en', {
        timeZoneName: 'short',
        timeZone: timeZoneName
      }).formatToParts(date).find(part => part.type === 'timeZoneName')?.value || '';
      return format(date, "EEEE, MMMM do 'at' h:mm a") + " " + timeZoneAbbr;
    } catch {
      return "Date unavailable";
    }
  };

  const validateForm = () => {
    const errors = { name: '', email: '', phone: '' };
    let isValid = true;
    if (!guestInfo.name || guestInfo.name.trim() === '') {
      errors.name = 'Name is required';
      isValid = false;
    } else if (guestInfo.name.trim().length < 2) {
      errors.name = 'Please enter your full name';
      isValid = false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!guestInfo.email || guestInfo.email.trim() === '') {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!emailRegex.test(guestInfo.email)) {
      errors.email = 'Please enter a valid email address';
      isValid = false;
    }
    if (guestInfo.phone && guestInfo.phone.trim() !== '') {
      const phoneDigits = guestInfo.phone.replace(/\D/g, '');
      if (phoneDigits.length < 10) {
        errors.phone = 'Please enter a valid phone number';
        isValid = false;
      }
    }
    setFormErrors(errors);
    return isValid;
  };

  const handleRegistration = async () => {
    console.log('[AIBootcamp] 🔥 REGISTRATION BUTTON CLICKED - DEBUGGING VALUES:', {
      sessionGroupId: sessionGroup?.id,
      isAuthenticated,
      hasLiveBootcampAccess,
      userHasAccess: courseData?.userHasAccess,
      user: user,
      userEmail: user?.email,
      userLoading: authLoadingFromAuth0,
      overallLoading: overallIsLoading,
      productSlugs
    });

    if (!sessionGroup?.id) {
      console.log('[AIBootcamp] ❌ No session group ID, aborting registration');
      return;
    }

    if (courseData?.userHasAccess === false) {
      console.log('[AIBootcamp] ❌ User does not have access to course');
      setApiError("Registration is currently not available for this course.");
      return;
    }

    if (!isAuthenticated) {
      console.log('[AIBootcamp] ❌ User not authenticated, redirecting to login');
      pushEvent({ event: ANALYTICS_EVENTS.PAYWALL_MODAL_VIEWED, courseId: courseData?.id, source: window.location.pathname, contextType: 'register_bootcamp_cohort', itemId: sessionGroup.id, reason: 'not_authenticated' });
      navigate(`/login?returnTo=${encodeURIComponent(window.location.pathname)}`, {
        state: buildAuthOverlayRouteState(location),
      });
      return;
    }
    if (!hasLiveBootcampAccess) {
      console.log('[AIBootcamp] ❌ User does not have live bootcamp access, redirecting to live bootcamp offer');
      pushEvent({ event: ANALYTICS_EVENTS.PAYWALL_MODAL_VIEWED, courseId: courseData?.id, source: window.location.pathname, contextType: 'register_bootcamp_cohort', itemId: sessionGroup.id, reason: 'live_bootcamp_required' });
      navigate('/ai-bot-builder-bootcamp');
      return;
    }

    console.log('[AIBootcamp] ✅ All checks passed, proceeding with registration');

    setApiError('');
    setIsRegistering(true);
    try {
      // CRITICAL FIX: Use unified user data for both Auth0 and custom JWT
      const currentUserId = user?.sub || user?.id || user?.userId;
      // Get email from user object (now from unified auth context)
      const currentEmail = user?.email;
      
      console.log('[AIBootcamp] Registration attempt:', {
        currentUserId,
        currentEmail,
        userEmail: user?.email
      });
      
      if (!currentEmail) {
        setApiError('Unable to determine user email for registration. Please try logging out and back in.');
        return;
      }
      
      const registrationData = { email: currentEmail };
      let headers = { 'Content-Type': 'application/json' };
      const token = await getApiToken();
      headers['Authorization'] = `Bearer ${token}`;
      const apiUrl = getApiBaseUrl();

      const response = await fetch(
        `${apiUrl}/courses/session-groups/${sessionGroup.id}/register`,
        { method: 'POST', headers, body: JSON.stringify(registrationData) }
      );
      if (!response.ok) {
        let errorMessage = `Registration failed: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch {}
        setApiError(errorMessage);
        return;
      }
      const storageData = {
        sessionGroupId: sessionGroup.id,
        timestamp: new Date().toISOString(),
        userId: currentUserId,
        email: currentEmail
      };
      localStorage.setItem(
        `ai-bootcamp-registered-${sessionGroup.id}`,
        JSON.stringify(storageData)
      );
      setIsRegistered(true);
      setApiError("");

      pushEvent({
        event: ANALYTICS_EVENTS.COURSE_REGISTER,
        courseType: 'bootcamp',
        groupId: sessionGroup.id,
        isAuthenticated,
        email: currentEmail
      });

      // Refresh session group data to get join URLs after registration
      if (isAuthenticated) {
        try {
          const token = await getApiToken();
          const apiUrl = getApiBaseUrl();
          const refreshResponse = await fetch(
            `${apiUrl}/courses/session-groups/${sessionGroup.id}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          if (refreshResponse.ok) {
            const updatedGroup = await refreshResponse.json();
            setSessionGroup(updatedGroup);
            console.log('[AIBootcamp] ✅ Session data refreshed successfully after registration');
          } else {
            console.error('[AIBootcamp] Failed to refresh session data after registration:', refreshResponse.status);
          }
        } catch (refreshError) {
          console.error('[AIBootcamp] Error refreshing session data after registration:', refreshError);
        }
      }
    } catch (err) {
      setApiError(err.message || 'Registration failed. Please try again later.');
    } finally {
      setIsRegistering(false);
    }
  };

  const handleUpcomingCohortRegistration = async (cohortId) => {
    if (!cohortId) return;

    if (courseData?.userHasAccess === false) {
      setApiError("Registration is currently not available for this course.");
      return;
    }

    if (!isAuthenticated) {
      pushEvent({ event: ANALYTICS_EVENTS.PAYWALL_MODAL_VIEWED, courseId: courseData?.id, source: window.location.pathname, contextType: 'register_bootcamp_cohort', itemId: cohortId, reason: 'not_authenticated' });
      navigate(`/login?returnTo=${encodeURIComponent(window.location.pathname)}`, {
        state: buildAuthOverlayRouteState(location),
      });
      return;
    }
    if (!hasLiveBootcampAccess) {
      pushEvent({ event: ANALYTICS_EVENTS.PAYWALL_MODAL_VIEWED, courseId: courseData?.id, source: window.location.pathname, contextType: 'register_bootcamp_cohort', itemId: cohortId, reason: 'live_bootcamp_required' });
      navigate('/ai-bot-builder-bootcamp');
      return;
    }

    setApiError('');
    setIsRegistering(true);
    try {
      // CRITICAL FIX: Use unified user data for both Auth0 and custom JWT
      const currentUserId = user?.sub || user?.id || user?.userId;
      // Get email from user object (now from unified auth context)
      const currentEmail = user?.email;
      
      console.log('[AIBootcamp] Upcoming cohort registration attempt:', {
        cohortId,
        currentUserId,
        currentEmail,
        userEmail: user?.email
      });
      
      if (!currentEmail) {
        setApiError('Unable to determine user email for registration. Please try logging out and back in.');
        return;
      }
      
      const registrationData = { email: currentEmail };
      let headers = { 'Content-Type': 'application/json' };
      const token = await getApiToken();
      headers['Authorization'] = `Bearer ${token}`;
      const apiUrl = getApiBaseUrl();

      const response = await fetch(
        `${apiUrl}/courses/session-groups/${cohortId}/register`,
        { method: 'POST', headers, body: JSON.stringify(registrationData) }
      );
      if (response.status === 409) {
        setRegisteredCohorts(prev => ({ ...prev, [cohortId]: true }));
        const storageData = {
          sessionGroupId: cohortId,
          timestamp: new Date().toISOString(),
          userId: currentUserId,
          email: currentEmail
        };
        localStorage.setItem(
          `ai-bootcamp-registered-${cohortId}`,
          JSON.stringify(storageData)
        );
        setApiError("You're already registered for this cohort!");
        return;
      }
      if (!response.ok) {
        let errorMessage = `Registration failed: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch {}
        throw new Error(errorMessage);
      }
      const storageData = {
        sessionGroupId: cohortId,
        timestamp: new Date().toISOString(),
        userId: currentUserId,
        email: currentEmail
      };
      localStorage.setItem(
        `ai-bootcamp-registered-${cohortId}`,
        JSON.stringify(storageData)
      );
      setRegisteredCohorts(prev => ({ ...prev, [cohortId]: true }));
    } catch (err) {
      setApiError(err.message || 'Registration failed. Please try again later.');
    } finally {
      setIsRegistering(false);
    }
  };

  const handleUnregister = async () => {
    if (!sessionGroup?.id || !isAuthenticated) return;
    try {
      const token = await getApiToken();
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      const apiUrl = getApiBaseUrl();
      const response = await fetch(
        `${apiUrl}/courses/session-groups/${sessionGroup.id}/unregister`,
        { method: 'DELETE', headers }
      );
      if (!response.ok) throw new Error(`Unregistration failed: ${response.status}`);
      localStorage.removeItem(`ai-bootcamp-registered-${sessionGroup.id}`);
      setIsRegistered(false);

      const currentEmail = user?.email;
      pushEvent({
        event: ANALYTICS_EVENTS.COURSE_UNREGISTER,
        courseType: 'bootcamp',
        groupId: sessionGroup.id,
        isAuthenticated: true,
        email: currentEmail
      });
    } catch {}
  };

  const handleUpcomingCohortUnregister = async (cohortId) => {
    if (!cohortId || !isAuthenticated) return;
    try {
      const token = await getApiToken();
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      const apiUrl = getApiBaseUrl();
      const response = await fetch(
        `${apiUrl}/courses/session-groups/${cohortId}/unregister`,
        { method: 'DELETE', headers }
      );
      if (!response.ok) throw new Error(`Unregistration failed: ${response.status}`);
      localStorage.removeItem(`ai-bootcamp-registered-${cohortId}`);
      setRegisteredCohorts(prev => {
        const updated = { ...prev };
        delete updated[cohortId];
        return updated;
      });
    } catch {}
  };

  if (overallIsLoading) {
    return <Loading />;
  }

  console.log('Bootcamp Pro Portal session group info:', {
    hasSessionGroup: !!sessionGroup,
    name: sessionGroup?.name,
    isRegistered: isRegistered,
    sessionCount: sessionGroup?.sessions?.length || 0
  });

  return (
    <div className="ai-bootcamp-pro-portal" data-testid="pro-portal">
      <Container>
        {/* First-run panel after purchase. Buyers land here rather than on a
            dead-end thank-you page, so the first thing they see is what to do
            next. Dismissible, and only shown when ?welcome=1 is present. */}
        {showWelcome && (
          <div className="ai-bootcamp-welcome" data-testid="portal-welcome" role="status">
            <div>
              <h2>You are in. Welcome to the bootcamp.</h2>
              <p>
                Your seat is confirmed and every session below is yours, including the recordings.
                Before the first session: install Claude Code or Codex and sign in, and have a few
                trades or markets in mind that you actually care about.
              </p>
              <p className="ai-bootcamp-welcome-help">
                Questions before we start? Reply to your receipt email and it comes straight to us.
              </p>
            </div>
            <button
              type="button"
              className="ai-bootcamp-welcome-dismiss"
              onClick={dismissWelcome}
              aria-label="Dismiss welcome message"
            >
              Got it
            </button>
          </div>
        )}
        <header className="ai-bootcamp-header">
          {courseData ? (
            <>
              <div className="ai-bootcamp-member-hero">
                <div className="ai-bootcamp-member-copy">
                  {courseData.title && (
                    <h1 className="ai-bootcamp-title">{courseData.title}</h1>
                  )}
                  {courseData.description && (
                    <div
                      className="ai-bootcamp-subtitle"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(courseData.description) }}
                    />
                  )}
                  {courseData.longDescription && (
                    <div
                      className="ai-bootcamp-long-description"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(courseData.longDescription) }}
                    />
                  )}
                </div>
                <div className="ai-bootcamp-member-media">
                  <img
                    src="/images/revenue/self-paced-bootcamp-archive.webp"
                    alt="AI Bootcamp replay portal preview"
                    width="1024"
                    height="572"
                    loading="eager"
                  />
                </div>
              </div>
              {sessionGroup && !isRegistered && (
                <div style={{ margin: "1.2rem auto 0.5rem auto", maxWidth: 400, textAlign: 'center' }}>
                  {courseData?.userHasAccess === false ? (
                    <Button
                      variant="primary"
                      className="edu-register-button ai-register-sparkle-btn"
                      style={{
                        width: "100%",
                        maxWidth: 400,
                        fontSize: "1.1rem",
                        padding: "0.8rem 2.5rem",
                        borderRadius: "30px",
                        marginBottom: "2rem",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        display: "block",
                        marginLeft: "auto",
                        marginRight: "auto",
                        opacity: 0.7,
                        cursor: "not-allowed"
                      }}
                      disabled
                    >
                      <i className="fas fa-lock me-2"></i>
                      You do not have access to register
                    </Button>
                  ) : (
                    <SparkleButton
                      className="edu-register-button ai-register-sparkle-btn"
                      style={{
                        width: "100%",
                        maxWidth: 400,
                        fontSize: "1.1rem",
                        padding: "0.8rem 2.5rem",
                        borderRadius: "30px",
                        marginBottom: "2rem",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        display: "block",
                        marginLeft: "auto",
                        marginRight: "auto"
                      }}
                      onClick={handleRegistration}
                      disabled={isRegistering}
                    >
                      {isRegistering ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Registering...
                        </>
                      ) : (
                        <>Register for the Next Cohort</>
                      )}
                    </SparkleButton>
                  )}
                  {apiError && (
                    <div style={{
                      color: "#ff6b6b",
                      background: "rgba(244,67,54,0.08)",
                      border: "1px solid #ff6b6b",
                      borderRadius: 8,
                      padding: "0.7rem 1rem",
                      marginTop: "-1.2rem",
                      marginBottom: "1rem",
                      fontSize: "1rem",
                      textAlign: "center"
                    }}>
                      <i className="fas fa-exclamation-circle me-2"></i>
                      {apiError}
                    </div>
                  )}
                </div>
              )}
              {sessionGroup && isRegistered && (
                <div 
                  ref={registrationConfirmationRef}
                  style={{ 
                    margin: "1.2rem auto 0.5rem auto", 
                    maxWidth: 500, 
                    textAlign: 'center',
                    background: "linear-gradient(135deg, var(--education-success-start, #28a745), var(--education-success-end, #20c997))",
                    borderRadius: "15px",
                    padding: "1.5rem",
                    color: "white",
                    boxShadow: "0 8px 25px rgba(40, 167, 69, 0.3)"
                  }}
                >
                  <div style={{ fontSize: "2.5rem", marginBottom: "0.5rem" }}>
                    🎉
                  </div>
                  <h3 style={{ 
                    fontSize: "1.4rem", 
                    marginBottom: "0.8rem", 
                    fontWeight: "700",
                    color: "white" 
                  }}>
                    Registration Successful!
                  </h3>
                  <p style={{ 
                    fontSize: "1.1rem", 
                    marginBottom: "1.2rem",
                    color: "rgba(255, 255, 255, 0.95)",
                    lineHeight: "1.5"
                  }}>
                    You're registered for the AI Bootcamp Pro! A confirmation email has been sent to{' '}
                    <strong>{user?.email}</strong>.
                  </p>
                  <div style={{
                    background: "var(--education-success-tip, rgba(255, 255, 255, 0.15))",
                    borderRadius: "10px",
                    padding: "1rem",
                    marginBottom: "1rem"
                  }}>
                    <p style={{ 
                      margin: 0, 
                      fontSize: "1rem",
                      color: "rgba(255, 255, 255, 0.9)"
                    }}>
                      <i className="fas fa-calendar-plus me-2"></i>
                      <strong>Don't forget to add the sessions to your calendar below!</strong>
                    </p>
                  </div>
                  {/* Quick Calendar Access */}
                  <div style={{ 
                    display: "flex", 
                    gap: "0.8rem", 
                    flexDirection: window.innerWidth <= 600 ? "column" : "row",
                    justifyContent: "center"
                  }}>
                    <a
                      href={sessionGroup?.sessions ? generateGroupICalFile(sessionGroup.sessions, sessionGroup) : '#'}
                      download={`ai-bootcamp-pro-all-sessions.ics`}
                      style={{
                        background: "var(--education-register-start, #FF8C00)",
                        color: "white",
                        padding: "0.8rem 1.2rem",
                        borderRadius: "8px",
                        textDecoration: "none",
                        fontSize: "0.95rem",
                        fontWeight: "500",
                        textAlign: "center",
                        transition: "all 0.3s ease",
                        border: "1px solid #FF7F00",
                        flex: "1",
                        minWidth: "0"
                      }}
                      onClick={() => {
                        const currentEmail = user?.email;
                        pushEvent({
                          event: ANALYTICS_EVENTS.COURSE_ADD_TO_CALENDAR,
                          courseType: 'bootcamp',
                          groupId: sessionGroup?.id,
                          calendarType: 'apple_outlook',
                          isAuthenticated: true,
                          email: currentEmail
                        });
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = "var(--education-register-end, #FF7F00)";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = "var(--education-register-start, #FF8C00)";
                      }}
                    >
                      📱 Apple/Outlook
                    </a>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        const currentEmail = user?.email;
                        pushEvent({
                          event: ANALYTICS_EVENTS.COURSE_ADD_TO_CALENDAR,
                          courseType: 'bootcamp',
                          groupId: sessionGroup?.id,
                          calendarType: 'google',
                          isAuthenticated: true,
                          email: currentEmail
                        });
                        if (sessionGroup?.sessions) {
                          sessionGroup.sessions.forEach(session => {
                            if (session && session.scheduledAt) {
                              const url = generateGoogleCalLink(session);
                              if (url && url !== '#') {
                                window.open(url, '_blank', 'noopener,noreferrer');
                              }
                            }
                          });
                        }
                      }}
                      style={{
                        background: "var(--education-calendar-google, #4285F4)",
                        color: "white",
                        padding: "0.8rem 1.2rem",
                        borderRadius: "8px",
                        textDecoration: "none",
                        fontSize: "0.95rem",
                        fontWeight: "500",
                        textAlign: "center",
                        transition: "all 0.3s ease",
                        border: "1px solid #3367d6",
                        flex: "1",
                        minWidth: "0"
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = "var(--education-calendar-google-hover, #3367d6)";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = "var(--education-calendar-google, #4285F4)";
                      }}
                    >
                      🗓️ Google Calendar
                    </a>
                  </div>
                </div>
              )}
            </>
          ) : !serviceUnavailable ? (
            <div className="ai-bootcamp-loading">
              <Spinner animation="border" role="status" className="mb-3" />
              <p>Loading bootcamp information...</p>
            </div>
          ) : null}
        </header>

        {serviceUnavailable && (
          <div className="ai-bootcamp-info-message">
            <div className="ai-info-card">
              <div className="ai-info-icon">
                <i className="fas fa-calendar-alt"></i>
              </div>
              <h3>Bootcamp Information</h3>
              <p>{courseLoadError || "We're currently updating our bootcamp schedule. Please check back soon for upcoming sessions."}</p>
              <Button
                variant="primary"
                onClick={handleRefresh}
                className="ai-refresh-button"
              >
                <i className="fas fa-sync-alt me-2"></i>
                Refresh
              </Button>
            </div>
          </div>
        )}

        {!serviceUnavailable && (
          <>
            {sessionGroup && (
              <section className="ai-bootcamp-content">
                {console.log('Passing to SessionGroupCard (Pro portal):', {
                  sessionGroup: sessionGroup,
                  sessions: sessionGroup?.sessions,
                  firstSession: sessionGroup?.sessions?.[0],
                  hasJoinUrl: sessionGroup?.sessions?.[0]?.joinUrl,
                  sessionGroupId: sessionGroup.id,
                  allSessionData: sessionGroup?.sessions?.map(s => ({
                    id: s.id,
                    title: s.title,
                    scheduledAt: s.scheduledAt,
                    joinUrl: s.joinUrl,
                    durationMinutes: s.durationMinutes
                  }))
                })}
                <SessionGroupCard
                  group={{
                    ...sessionGroup,
                    description: sessionGroup?.description
                      ? DOMPurify.sanitize(sessionGroup.description)
                      : undefined,
                    longDescription: sessionGroup?.longDescription
                      ? DOMPurify.sanitize(sessionGroup.longDescription)
                      : undefined,
                    sessions: sessionGroup?.sessions?.map(session => ({
                      ...session,
                      description: session.description
                        ? DOMPurify.sanitize(session.description)
                        : undefined,
                      longDescription: session.longDescription
                        ? DOMPurify.sanitize(session.longDescription)
                        : undefined,
                    })),
                  }}
                  isCurrent={true}
                  isRegistered={isRegistered}
                  isRegistering={isRegistering}
                  isAuthenticated={isAuthenticated}
                  user={user}
                  apiError={apiError}
                  formErrors={formErrors}
                  guestInfo={guestInfo}
                  setGuestInfo={setGuestInfo}
                  handleRegistration={handleRegistration}
                  handleUnregister={handleUnregister}
                  handleUpcomingCohortRegistration={handleUpcomingCohortRegistration}
                  handleUpcomingCohortUnregister={handleUpcomingCohortUnregister}
                  formatWorkshopDate={formatWorkshopDate}
                  registrationConfirmationRef={registrationConfirmationRef}
                  courseUserHasAccess={courseData?.userHasAccess}
                  courseRequiresPro={true}
                  userHasProSubscription={hasLiveBootcampAccess}
                  requiredAccessLabel="Live Bootcamp access"
                  upgradePath="/ai-bot-builder-bootcamp"
                  upgradeCta="Upgrade to Live Bootcamp"
                  courseIsPublic={false}
                  navigate={navigate}
                  loginWithRedirect={loginWithRedirect}
                />
              </section>
            )}

            {futureSessionGroups && futureSessionGroups.length > 0 && (
              <section className="ai-future-bootcamps">
                <h3 className="ai-section-title">Upcoming</h3>
                {futureSessionGroups.map(group => (
                  <SessionGroupCard
                    key={group.id}
                    group={{
                      ...group,
                      description: group?.description
                        ? DOMPurify.sanitize(group.description)
                        : undefined,
                      longDescription: group?.longDescription
                        ? DOMPurify.sanitize(group.longDescription)
                        : undefined,
                      sessions: group?.sessions?.map(session => ({
                        ...session,
                        description: session.description
                          ? DOMPurify.sanitize(session.description)
                          : undefined,
                        longDescription: session.longDescription
                          ? DOMPurify.sanitize(session.longDescription)
                          : undefined,
                      })),
                    }}
                    isCurrent={false}
                    isRegistered={registeredCohorts[group.id] || (group.isRegistered && isAuthenticated)}
                    isRegistering={isRegistering}
                    isAuthenticated={isAuthenticated}
                    user={user}
                    apiError={apiError}
                    formErrors={formErrors}
                    guestInfo={guestInfo}
                    setGuestInfo={setGuestInfo}
                    handleUpcomingCohortRegistration={handleUpcomingCohortRegistration}
                    handleUpcomingCohortUnregister={handleUpcomingCohortUnregister}
                    formatWorkshopDate={formatWorkshopDate}
                    courseUserHasAccess={courseData?.userHasAccess}
                    courseRequiresPro={true}
                    userHasProSubscription={hasLiveBootcampAccess}
                    requiredAccessLabel="Live Bootcamp access"
                    upgradePath="/ai-bot-builder-bootcamp"
                    upgradeCta="Upgrade to Live Bootcamp"
                    courseIsPublic={false}
                    navigate={navigate}
                    loginWithRedirect={loginWithRedirect}
                  />
                ))}
              </section>
            )}

            {pastSessionGroups.length > 0 && (
              <PastSessionGroups
                pastSessionGroups={pastSessionGroups.map(group => ({
                  ...group,
                  description: group?.description
                    ? DOMPurify.sanitize(group.description)
                    : undefined,
                  longDescription: group?.longDescription
                    ? DOMPurify.sanitize(group.longDescription)
                    : undefined,
                  sessions: group?.sessions?.map(session => ({
                    ...session,
                    description: session.description
                      ? DOMPurify.sanitize(session.description)
                      : undefined,
                    longDescription: session.longDescription
                      ? DOMPurify.sanitize(session.longDescription)
                      : undefined,
                  })),
                }))}
                formatWorkshopDate={formatWorkshopDate}
                isAuthenticated={isAuthenticated}
                courseRequiresPro={true}
                userHasProSubscription={hasBootcampContentAccess}
                requiredAccessLabel="AI Bootcamp access"
                upgradePath="/ai-bot-builder-bootcamp"
                upgradeCta="Get AI Bootcamp"
                courseIsPublic={false}
                navigate={navigate}
                loginWithRedirect={loginWithRedirect}
              />
            )}

            {courseData && !sessionGroup && futureSessionGroups.length === 0 && pastSessionGroups.length === 0 && !serviceUnavailable && (
              <div className="ai-no-sessions-message">
                <Card className="ai-info-card">
                  <Card.Body className="text-center py-4">
                    <i className="fas fa-calendar-alt ai-info-icon mb-3"></i>
                    <h3>Bootcamp Cohorts Coming Soon</h3>
                    <p>We're preparing new bootcamp cohorts. Check back soon for updates.</p>
                  </Card.Body>
                </Card>
              </div>
            )}
          </>
        )}
      </Container>
    </div>
  );
};

export default AIBootcampProPortal; 
